import {
  FocusEvent,
  KeyboardEvent,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Translate2 } from 'common'
import { KeyBoardKeys } from '../../../../../ui-components/util/Interfaces'
import {
  TableColumn,
  TableColumnOption,
  VehicleDropdownOnChangeProps,
} from '../../interfaces'
import { IdValueGeneric } from 'interfaces'
import {
  getCurrentCalendarYear,
  getYearRangeLabel,
  groupYearsIntoDecadeColumns,
  splitYearOptions,
  YEAR_PRIMARY_BOUNDARY,
} from './yearPickerUtils'

type YearPickerPage = 'primary' | 'extended'

interface YearDesktopPickerProps {
  columns: Array<TableColumn>
  isActive: boolean
  onSelectItem: (selection: VehicleDropdownOnChangeProps) => void
  onCancel: () => void
  selectedOption?: IdValueGeneric<number, string>
  tableRef: RefObject<HTMLDivElement>
}

const toSelection = (
  option: TableColumnOption
): VehicleDropdownOnChangeProps => ({
  selection: option,
  selectionType: {
    id: option.typeId,
    value: option.typeName,
  },
})

const toYearNumber = (value: string | number): number => Number(value)

const findCellId = (
  gridCols: Array<TableColumn>,
  pageName: YearPickerPage,
  year: number
): string | null => {
  for (let colIndex = 0; colIndex < gridCols.length; colIndex += 1) {
    const options = gridCols[colIndex].options ?? []
    for (let rowIndex = 0; rowIndex < options.length; rowIndex += 1) {
      if (toYearNumber(options[rowIndex].value) === year) {
        return `${pageName}-${colIndex}-${rowIndex}`
      }
    }
  }
  return null
}

export const YearDesktopPicker = ({
  columns,
  isActive,
  onSelectItem,
  onCancel,
  selectedOption,
  tableRef,
}: YearDesktopPickerProps): ReactElement => {
  const { primaryYears, extendedYears } = useMemo(
    () => splitYearOptions(columns),
    [columns]
  )
  const hasExtendedYears = extendedYears.length > 0
  const [page, setPage] = useState<YearPickerPage>('primary')
  const rightNavRef = useRef<HTMLButtonElement>(null)
  const focusTargetRef = useRef<string | null>(null)

  const primaryGridColumns = useMemo(
    () => groupYearsIntoDecadeColumns(primaryYears),
    [primaryYears]
  )
  const extendedGridColumns = useMemo(
    () => groupYearsIntoDecadeColumns(extendedYears),
    [extendedYears]
  )

  const pageYears = page === 'primary' ? primaryYears : extendedYears
  const gridColumns =
    page === 'primary' ? primaryGridColumns : extendedGridColumns
  const rangeLabel = useMemo(() => getYearRangeLabel(pageYears), [pageYears])
  const showBackNav = page === 'extended'
  const showNextNav = page === 'primary' && hasExtendedYears

  useEffect(() => {
    if (!isActive) {
      setPage('primary')
      focusTargetRef.current = null
      return
    }

    const selectedYear = selectedOption?.value
      ? toYearNumber(selectedOption.value)
      : null

    if (
      selectedYear !== null &&
      selectedYear < YEAR_PRIMARY_BOUNDARY &&
      hasExtendedYears
    ) {
      setPage('extended')
      const targetId = findCellId(extendedGridColumns, 'extended', selectedYear)
      if (targetId) {
        focusTargetRef.current = targetId
      }
      return
    }

    setPage('primary')
    if (selectedYear !== null) {
      const targetId = findCellId(primaryGridColumns, 'primary', selectedYear)
      if (targetId) {
        focusTargetRef.current = targetId
      }
    }
  }, [
    isActive,
    selectedOption?.id,
    selectedOption?.value,
    hasExtendedYears,
    primaryGridColumns,
    extendedGridColumns,
  ])

  useEffect(() => {
    if (!isActive || focusTargetRef.current) return undefined

    if (document.activeElement instanceof HTMLInputElement) return undefined

    const currentYear = getCurrentCalendarYear()
    const currentYearCell = gridColumns
      .flatMap((col, colIndex) =>
        (col.options ?? []).map((option, rowIndex) => ({
          option,
          colIndex,
          rowIndex,
        }))
      )
      .find(({ option }) => toYearNumber(option.value) === currentYear)

    let targetId: string | null = null
    if (currentYearCell) {
      targetId = `${page}-${currentYearCell.colIndex}-${currentYearCell.rowIndex}`
    } else if (gridColumns[0]?.options?.[0]) {
      targetId = `${page}-0-0`
    }

    if (!targetId) return undefined

    const frame = requestAnimationFrame(() => {
      if (document.activeElement instanceof HTMLInputElement) return
      const el = document.getElementById(targetId)
      el?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(frame)
  }, [isActive, page, gridColumns, selectedOption?.value])

  const focusElementById = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (!el?.isConnected) return
    el.focus({ preventScroll: true })
  }, [])

  const handleGridKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const currentElement = e.currentTarget
    const previousSibling = currentElement.previousElementSibling as HTMLElement
    const nextSibling = currentElement.nextElementSibling as HTMLElement
    const colIndex = Number(currentElement.dataset.column)
    const rowIndex = Number(currentElement.dataset.row)

    if (e.key === KeyBoardKeys.ArrowUp) {
      e.preventDefault()
      if (previousSibling) {
        previousSibling.focus({ preventScroll: true })
        return
      }
      const previousColumn = document.getElementById(
        `${page}-${colIndex - 1}-${rowIndex}`
      )
      previousColumn?.focus({ preventScroll: true })
      return
    }

    if (e.key === KeyBoardKeys.ArrowDown) {
      e.preventDefault()
      if (nextSibling) {
        nextSibling.focus({ preventScroll: true })
        return
      }

      const isLastRowInColumn = !nextSibling
      const isPrimaryBoundaryYear =
        page === 'primary' &&
        hasExtendedYears &&
        toYearNumber(currentElement.textContent ?? '') === YEAR_PRIMARY_BOUNDARY

      if (isLastRowInColumn && isPrimaryBoundaryYear) {
        rightNavRef.current?.focus({ preventScroll: true })
        return
      }

      const nextColumn = document.getElementById(`${page}-${colIndex + 1}-0`)
      nextColumn?.focus({ preventScroll: true })
      return
    }

    if (e.key === KeyBoardKeys.ArrowLeft) {
      e.preventDefault()
      const previousCell = document.getElementById(
        `${page}-${colIndex - 1}-${rowIndex}`
      )
      previousCell?.focus({ preventScroll: true })
      return
    }

    if (e.key === KeyBoardKeys.ArrowRight) {
      e.preventDefault()
      const nextCell = document.getElementById(
        `${page}-${colIndex + 1}-${rowIndex}`
      )
      nextCell?.focus({ preventScroll: true })
      return
    }

    if (e.key === KeyBoardKeys.Enter || e.key === KeyBoardKeys.Tab) {
      e.preventDefault()
      currentElement.click()
    }
  }

  const handleNavKeyDown = (
    e: KeyboardEvent<HTMLButtonElement>,
    direction: 'left' | 'right'
  ): void => {
    if (e.key === KeyBoardKeys.Enter || e.key === KeyBoardKeys.Tab) {
      e.preventDefault()
      if (direction === 'left' && page === 'extended') {
        setPage('primary')
        const targetId = findCellId(
          primaryGridColumns,
          'primary',
          YEAR_PRIMARY_BOUNDARY
        )
        if (targetId) focusTargetRef.current = targetId
      }
      if (direction === 'right' && page === 'primary' && hasExtendedYears) {
        setPage('extended')
        focusTargetRef.current = 'extended-0-0'
      }
      return
    }

    if (e.key === KeyBoardKeys.ArrowUp && direction === 'right') {
      e.preventDefault()
      const targetId = findCellId(
        primaryGridColumns,
        'primary',
        YEAR_PRIMARY_BOUNDARY
      )
      if (targetId) focusElementById(targetId)
    }
  }

  useEffect(() => {
    if (!focusTargetRef.current || !isActive) return undefined
    const targetId = focusTargetRef.current
    focusTargetRef.current = null
    const frame = requestAnimationFrame(() => focusElementById(targetId))
    return () => cancelAnimationFrame(frame)
  }, [page, isActive, focusElementById])

  const handleOptionClick = (option: TableColumnOption): void => {
    const isSelected =
      option.id === selectedOption?.id && option.value === selectedOption?.value
    if (isSelected) {
      onCancel()
      return
    }
    onSelectItem(toSelection(option))
  }

  const handleWrapperFocus = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget || !isActive) return
      const targetId = findCellId(gridColumns, page, getCurrentCalendarYear())
      if (targetId) focusElementById(targetId)
    },
    [focusElementById, gridColumns, isActive, page]
  )

  const goToExtendedPage = () => {
    if (!hasExtendedYears) return
    setPage('extended')
    focusTargetRef.current = 'extended-0-0'
  }

  const goToPrimaryPage = () => {
    setPage('primary')
    const targetId = findCellId(
      primaryGridColumns,
      'primary',
      YEAR_PRIMARY_BOUNDARY
    )
    if (targetId) focusTargetRef.current = targetId
  }

  return (
    <Container ref={tableRef} tabIndex={0} onFocus={handleWrapperFocus}>
      <Header>
        <NavSlot>
          {showBackNav && (
            <NavButton
              type="button"
              onClick={goToPrimaryPage}
              onKeyDown={(e) => handleNavKeyDown(e, 'left')}
              aria-label={Translate2('showNewerYears')}
            >
              <ChevronLeft fontSize="small" />
            </NavButton>
          )}
        </NavSlot>
        <RangeLabel>{rangeLabel}</RangeLabel>
        <NavSlot $alignEnd>
          {showNextNav && (
            <NavButton
              ref={rightNavRef}
              type="button"
              onClick={goToExtendedPage}
              onKeyDown={(e) => handleNavKeyDown(e, 'right')}
              aria-label={Translate2('showEarlierYears')}
            >
              <ChevronRight fontSize="small" />
            </NavButton>
          )}
        </NavSlot>
      </Header>
      <Grid>
        {gridColumns.map((col, colIndex) => {
          if (!col.options?.length) return null
          return (
            <Column key={`${page}_${col.id}`}>
              {col.options.map((option, rowIndex) => {
                const isSelected =
                  option.id === selectedOption?.id &&
                  option.value === selectedOption?.value
                const cellId = `${page}-${colIndex}-${rowIndex}`
                return (
                  <MenuOption
                    key={`${option.id}_${option.value}`}
                    id={cellId}
                    data-column={colIndex}
                    data-row={rowIndex}
                    selected={isSelected}
                    tabIndex={0}
                    onClick={() => handleOptionClick(option)}
                    onKeyDown={handleGridKeyDown}
                  >
                    {option.value}
                  </MenuOption>
                )
              })}
            </Column>
          )
        })}
      </Grid>
    </Container>
  )
}

interface MenuOptionProps {
  selected: boolean
}

const Container = styled.div`
  margin-top: 2px;
  color: ${(p) => p.theme.colors.text};
  border: ${(p) => p.theme.fieldBorder};
  background-color: #fff;
  width: max-content;
  min-width: 100%;
`

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  width: 100%;
`

const NavSlot = styled.div<{ $alignEnd?: boolean }>`
  display: flex;
  justify-content: ${(p) => (p.$alignEnd ? 'flex-end' : 'flex-start')};
  min-width: 28px;
`

const RangeLabel = styled.span`
  font-weight: 700;
  font-size: 14px;
`

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  color: ${(p) => p.theme.colors.black};

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 2px;
  }
`

const Grid = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-height: 330px;
  overflow: auto;
  padding: 4px 0;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 52px;
`

const MenuOption = styled.div<MenuOptionProps>`
  padding: 6px 10px;
  font-weight: 400;
  font-size: 12px;
  background-color: ${(p) => (p.selected ? p.theme.colors.primary : 'none')};
  color: ${(p) => p.theme.colors.black};
  cursor: pointer;
  text-align: center;

  &:hover,
  &:focus {
    background: ${(p) =>
      p.selected ? p.theme.colors.primary : p.theme.colors.disabledBackground};
    border: none;
    outline-style: none;
  }

  &:active {
    background: ${(p) => (p.selected ? p.theme.colors.primary : '#777777')};
  }
`
